import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Activity,
  ArrowLeft,
  BarChart3,
  CalendarDays,
  CheckCircle2,
  Coins,
  Edit2,
  FlaskConical,
  LayoutDashboard,
  Loader2,
  MessageCircle,
  Pill,
  Plus,
  Radio,
  ScanSearch,
  Search,
  Settings,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Stethoscope,
  Trash2,
  Users,
  Video,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useApiClient } from '../../lib/apiClient';
import { useI18n } from '../../i18n/LanguageContext';
import {
  NAVIGATION_MENU_PERMISSION_GROUPS,
  OPERATIONAL_PERMISSION_GROUPS,
  PATIENT_FILE_PERMISSION_GROUPS,
  SETTINGS_PERMISSION_GROUPS,
  type PermissionGroup,
} from '../../lib/permissionCatalog';

type Role = {
  id: number;
  name: string;
  description: string;
  permissionCodes: string[];
};

type Permission = {
  id: number;
  code: string;
  description: string;
};

const ROLE_TYPES: Array<{ value: string; label: string }> = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'DOCTOR', label: 'Doctor' },
  { value: 'NURSE', label: 'Nurse' },
  { value: 'RECEPTIONIST', label: 'Receptionist' },
  { value: 'PATIENT', label: 'Patient' },
];

const CATALOG_PERMISSION_GROUPS = [
  ...OPERATIONAL_PERMISSION_GROUPS,
  ...NAVIGATION_MENU_PERMISSION_GROUPS,
  ...PATIENT_FILE_PERMISSION_GROUPS,
  ...SETTINGS_PERMISSION_GROUPS,
];

const CATALOG_PERMISSION_CODES = new Set(CATALOG_PERMISSION_GROUPS.flatMap((group) => group.permissionCodes));

function normalizeCatalogPermissionCodes(codes: string[]) {
  return Array.from(new Set(codes.map((code) => String(code || '').trim()).filter((code) => CATALOG_PERMISSION_CODES.has(code)))).sort();
}

export default function SettingsRoles() {
  const navigate = useNavigate();
  const { roleId } = useParams();
  const { fetchJson, fetchApi } = useApiClient();
  const { tr, t } = useI18n();

  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [form, setForm] = useState<{ id?: number; name: string; description: string; permissionCodes: string[] }>({
    name: '',
    description: '',
    permissionCodes: [],
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [roleData, permData] = await Promise.all([
          fetchJson<Role[]>('/roles'),
          fetchJson<Permission[]>('/roles/permissions'),
        ]);
        setRoles(roleData);
        setPermissions(permData);
      } catch (err: any) {
        setFailure(err.message ?? tr('Failed to load roles'));
      } finally {
        setLoading(false);
      }
    })();
  }, [fetchJson]);

  useEffect(() => {
    if (!roleId) {
      setForm({ id: undefined, name: '', description: '', permissionCodes: [] });
      setErrors({});
      return;
    }
    if (roleId === 'new') {
      setForm({ id: undefined, name: '', description: '', permissionCodes: [] });
      setErrors({});
      setFailure(null);
      setBanner(null);
      return;
    }
    const selected = roles.find((role) => String(role.id) === roleId) || null;
    if (selected) {
      setForm({
        id: selected.id,
        name: selected.name,
        description: selected.description,
        permissionCodes: normalizeCatalogPermissionCodes(selected.permissionCodes || []),
      });
      setErrors({});
      setFailure(null);
      setBanner(null);
    }
  }, [roleId, roles]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return roles.filter((role) => role.name.toLowerCase().includes(q) || role.description.toLowerCase().includes(q));
  }, [roles, search]);

  const handleFormChange = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[key as string];
      return next;
    });
  };

  function validate() {
    const next: Record<string, string> = {};
    if (!form.name) next.name = tr('Role is required');
    if (!form.description.trim()) next.description = tr('Description is required');
    return next;
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaving(true);
    setFailure(null);
    setBanner(null);

    try {
      const payload = {
        name: form.name,
        description: form.description.trim(),
        permissionCodes: form.permissionCodes,
      };
      const path = form.id ? `/roles/${form.id}` : '/roles';
      const res = await fetchApi(path, {
        method: form.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`Save failed (${res.status})`);
      const saved = (await res.json()) as Role;
      setRoles((prev) => (prev.some((r) => r.id === saved.id) ? prev.map((r) => (r.id === saved.id ? saved : r)) : [...prev, saved]));
      setBanner(tr('Role saved'));
      if (!form.id) {
        navigate(`/settings/roles/${saved.id}`, { replace: true });
      }
    } catch (err: any) {
      setFailure(err.message ?? tr('Unable to save role'));
    } finally {
      setSaving(false);
    }
  }

  function handleDelete(id: number) {
    void id;
    setFailure(null);
    setBanner(tr('Role deletion is not available yet. You can edit the role or remove its permissions.'));
  }

  const unusedRoleOptions = ROLE_TYPES.filter((r) => !roles.some((existing) => existing.name === r.value) || form.name === r.value);
  const selectedRole = roleId && roleId !== 'new'
    ? roles.find((role) => String(role.id) === roleId) || null
    : null;

  const permissionSet = new Set(form.permissionCodes);
  const togglePermissionCodes = (codes: string[], enabled: boolean) => {
    const next = new Set(permissionSet);
    for (const code of codes) {
      if (enabled) {
        next.add(code);
      } else {
        next.delete(code);
      }
    }
    handleFormChange('permissionCodes', Array.from(next).sort());
  };

  if (roleId) {
    return (
      <RoleDetailPage
        navigate={navigate}
        tr={tr}
        t={t}
        loading={loading}
        failure={failure}
        banner={banner}
        saving={saving}
        roleId={roleId}
        selectedRole={selectedRole}
        form={form}
        errors={errors}
        permissions={permissions}
        coreItems={OPERATIONAL_PERMISSION_GROUPS}
        navItems={NAVIGATION_MENU_PERMISSION_GROUPS}
        patientTabs={PATIENT_FILE_PERMISSION_GROUPS}
        settingsItems={SETTINGS_PERMISSION_GROUPS}
        unusedRoleOptions={unusedRoleOptions}
        setBanner={setBanner}
        setFailure={setFailure}
        setSaving={setSaving}
        setForm={setForm}
        setErrors={setErrors}
        handleFormChange={handleFormChange}
        togglePermissionCodes={togglePermissionCodes}
        handleSave={handleSave}
      />
    );
  }

  return (
    <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/settings')} className="p-2 hover:bg-slate-100 rounded-full">
              <ArrowLeft size={20} />
            </button>
          <div>
            <h1 className="text-2xl font-semibold text-ink">{tr('Roles & Permissions')}</h1>
            <p className="text-slate-500 text-sm">{tr('Define access levels and map permissions.')}</p>
          </div>
          </div>
        <button onClick={() => navigate('/settings/roles/new')} className="px-4 py-2 bg-ink text-white rounded-lg shadow-soft flex items-center gap-2">
          <Plus size={16} /> {tr('Add Role')}
        </button>
      </div>

      {banner && (
        <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 px-3 py-2 rounded-lg">
          <CheckCircle2 size={16} /> {banner}
        </div>
      )}
      {failure && (
        <div className="flex items-center gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-100 px-3 py-2 rounded-lg">
          <ShieldAlert size={16} /> {failure}
        </div>
      )}

      <div className="card">
        <div className="p-4 border-b border-slate-100 flex items-center gap-2">
          <Search size={16} className="text-slate-400" />
          <input
            className="w-full outline-none text-sm"
            placeholder={tr('Search roles...')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <table className="w-full text-sm text-left">
          <thead className="bg-slate-50 text-slate-500 font-medium">
            <tr>
              <th className="p-4">{tr('Role')}</th>
              <th className="p-4">{tr('Description')}</th>
              <th className="p-4">{tr('Permissions')}</th>
              <th className="p-4 text-right">{tr('Actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td colSpan={4} className="p-6 text-center text-slate-500">
                  <span className="inline-flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin" /> {t('common.loading', 'Loading...')}
                  </span>
                </td>
              </tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={4} className="p-6 text-center text-slate-500">
                  {tr('No roles found.')}
                </td>
              </tr>
            )}
            {!loading &&
              filtered.map((role) => {
                const visiblePermissionCodes = normalizeCatalogPermissionCodes(role.permissionCodes || []);
                return (
                  <tr key={role.id} className="hover:bg-slate-50">
                    <td className="p-4 font-medium text-ink">
                      <div className="flex items-center gap-2">
                        <div className="h-9 w-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                          <Shield size={18} />
                        </div>
                        <span>{role.name}</span>
                      </div>
                    </td>
                    <td className="p-4 text-slate-600">{role.description}</td>
                    <td className="p-4 text-slate-600">
                      {visiblePermissionCodes.length ? (
                        <div className="flex flex-wrap gap-1">
                          {visiblePermissionCodes.slice(0, 4).map((code) => (
                            <span key={code} className="px-2 py-1 rounded-full bg-slate-100 text-xs text-slate-700">
                              {code}
                            </span>
                          ))}
                          {visiblePermissionCodes.length > 4 && (
                            <span className="text-xs text-slate-500">+{visiblePermissionCodes.length - 4} {tr('more')}</span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-500">{tr('No permissions')}</span>
                      )}
                    </td>
                    <td className="p-4 text-right flex justify-end gap-2">
                      <button className="p-1 text-slate-400 hover:text-ink" onClick={() => navigate(`/settings/roles/${role.id}`)} aria-label="Edit role">
                        <Edit2 size={16} />
                      </button>
                      <button className="p-1 text-slate-400 hover:text-rose-600" onClick={() => handleDelete(role.id)} aria-label="Delete role">
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

    </div>
  );
}

function RoleDetailPage({
  navigate,
  tr,
  t,
  loading,
  failure,
  banner,
  saving,
  roleId,
  selectedRole,
  form,
  errors,
  permissions,
  coreItems,
  navItems,
  patientTabs,
  settingsItems,
  unusedRoleOptions,
  setBanner,
  setFailure,
  setSaving,
  setForm,
  setErrors,
  handleFormChange,
  togglePermissionCodes,
  handleSave,
}: {
  navigate: ReturnType<typeof useNavigate>;
  tr: (phrase: string) => string;
  t: (key: string, fallback?: string) => string;
  loading: boolean;
  failure: string | null;
  banner: string | null;
  saving: boolean;
  roleId: string;
  selectedRole: Role | null;
  form: { id?: number; name: string; description: string; permissionCodes: string[] };
  errors: Record<string, string>;
  permissions: Permission[];
  coreItems: PermissionGroup[];
  navItems: PermissionGroup[];
  patientTabs: PermissionGroup[];
  settingsItems: PermissionGroup[];
  unusedRoleOptions: Array<{ value: string; label: string }>;
  setBanner: (value: string | null) => void;
  setFailure: (value: string | null) => void;
  setSaving: (value: boolean) => void;
  setForm: React.Dispatch<React.SetStateAction<{ id?: number; name: string; description: string; permissionCodes: string[] }>>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  handleFormChange: <K extends keyof typeof form>(key: K, value: (typeof form)[K]) => void;
  togglePermissionCodes: (codes: string[], enabled: boolean) => void;
  handleSave: (e: React.FormEvent) => Promise<void>;
}) {
  const permissionSet = new Set(form.permissionCodes);
  const permissionDescriptions = new Map(permissions.map((permission) => [permission.code, permission.description]));
  const corePermissionCards = coreItems.map((item) => ({
    title: item.title,
    description: item.description,
    codes: item.permissionCodes,
    codeLabel: item.permissionCodes.join(' · '),
    icon: resolvePermissionIcon(item.permissionCodes[0] || ''),
  }));
  const navPermissionCards = navItems.map((item) => ({
    title: item.title,
    description: item.description,
    codes: item.permissionCodes,
    codeLabel: item.permissionCodes.join(' · '),
    icon: resolveNavIcon(item.title),
  }));
  const patientPermissionCards = patientTabs.map((item) => ({
    title: item.title,
    description: item.description,
    codes: item.permissionCodes,
    codeLabel: item.permissionCodes.join(' · '),
    icon: resolvePatientTabIcon(item.title),
  }));
  const settingsPermissionCards = settingsItems.map((item) => ({
    title: item.title,
    description: item.description,
    codes: item.permissionCodes,
    codeLabel: item.permissionCodes.join(' · '),
    icon: resolveSettingsIcon(item.title),
  }));
  const allPermissionCards = CATALOG_PERMISSION_GROUPS.map((item) => ({
    title: item.title,
    description: item.permissionCodes.map((code) => permissionDescriptions.get(code)).filter(Boolean).join(' ') || item.description,
    codes: item.permissionCodes,
    codeLabel: item.permissionCodes.join(' · '),
    icon: resolvePermissionIcon(item.permissionCodes[0] || ''),
  }));
  const allPermissionsSelected = allPermissionCards.every((item) => item.codes.every((code) => permissionSet.has(code)));
  const corePermissionsSelected = corePermissionCards.every((item) => item.codes.every((code) => permissionSet.has(code)));
  const navPermissionsSelected = navPermissionCards.every((item) => item.codes.every((code) => permissionSet.has(code)));
  const patientPermissionsSelected = patientPermissionCards.every((item) => item.codes.every((code) => permissionSet.has(code)));
  const settingsPermissionsSelected = settingsPermissionCards.every((item) => item.codes.every((code) => permissionSet.has(code)));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/settings/roles')} className="p-2 hover:bg-slate-100 rounded-full">
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-ink">
              {roleId === 'new' ? tr('Add role') : selectedRole ? selectedRole.name : tr('Role details')}
            </h1>
            <p className="text-slate-500 text-sm">
              {roleId === 'new'
                ? tr('Create a new role and assign permissions.')
                : tr('Define access levels, navigation access, and patient-file tabs.')}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/settings/roles/new')}
          className="px-4 py-2 bg-ink text-white rounded-lg shadow-soft flex items-center gap-2"
        >
          <Plus size={16} /> {tr('Add Role')}
        </button>
      </div>

      {banner && (
        <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 px-3 py-2 rounded-lg">
          <CheckCircle2 size={16} /> {banner}
        </div>
      )}
      {failure && (
        <div className="flex items-center gap-2 text-sm text-rose-700 bg-rose-50 border border-rose-100 px-3 py-2 rounded-lg">
          <ShieldAlert size={16} /> {failure}
        </div>
      )}

      {loading ? (
        <div className="card p-6 text-center text-slate-500">
          <span className="inline-flex items-center gap-2">
            <Loader2 size={16} className="animate-spin" /> {t('common.loading', 'Loading...')}
          </span>
        </div>
      ) : !selectedRole && roleId !== 'new' ? (
        <div className="card p-6 text-slate-600">
          {tr('Role not found.')}
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-6">
          <div className="card p-6 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Role"
                required
                value={form.name}
                error={errors.name}
                onChange={(v) => handleFormChange('name', v)}
                asSelect
                options={unusedRoleOptions}
                disabled={roleId !== 'new'}
              />
              <Input
                label="Description"
                required
                value={form.description}
                error={errors.description}
                onChange={(v) => handleFormChange('description', v)}
                placeholder="What this role can do"
              />
            </div>
          </div>

          <PermissionCardSection
            title="All permissions"
            description="Canonical permissions available in the current Elen1 workspace."
            selectedCount={form.permissionCodes.length}
            items={allPermissionCards}
            permissionSet={permissionSet}
            onToggle={(codes) => togglePermissionCodes(codes, !codes.every((code) => permissionSet.has(code)))}
            onToggleAll={() => togglePermissionCodes(allPermissionCards.flatMap((item) => item.codes), !allPermissionsSelected)}
            allSelected={allPermissionsSelected}
          />

          <PermissionCardSection
            title="Navigation menu"
            description="Map permissions to the main sidebar links."
            selectedCount={navPermissionCards.filter((item) => item.codes.every((code) => permissionSet.has(code))).length}
            items={navPermissionCards}
            permissionSet={permissionSet}
            onToggle={(codes) => togglePermissionCodes(codes, !codes.every((code) => permissionSet.has(code)))}
            onToggleAll={() => togglePermissionCodes(navPermissionCards.flatMap((item) => item.codes), !navPermissionsSelected)}
            allSelected={navPermissionsSelected}
          />

          <PermissionCardSection
            title="Patient file"
            description="Map permissions to the patient-chart tabs."
            selectedCount={patientPermissionCards.filter((item) => item.codes.every((code) => permissionSet.has(code))).length}
            items={patientPermissionCards}
            permissionSet={permissionSet}
            onToggle={(codes) => togglePermissionCodes(codes, !codes.every((code) => permissionSet.has(code)))}
            onToggleAll={() => togglePermissionCodes(patientPermissionCards.flatMap((item) => item.codes), !patientPermissionsSelected)}
            allSelected={patientPermissionsSelected}
          />

          <PermissionCardSection
            title="Core actions"
            description="Map permissions to backend capabilities used by current Elen1 workflows."
            selectedCount={corePermissionCards.filter((item) => item.codes.every((code) => permissionSet.has(code))).length}
            items={corePermissionCards}
            permissionSet={permissionSet}
            onToggle={(codes) => togglePermissionCodes(codes, !codes.every((code) => permissionSet.has(code)))}
            onToggleAll={() => togglePermissionCodes(corePermissionCards.flatMap((item) => item.codes), !corePermissionsSelected)}
            allSelected={corePermissionsSelected}
          />

          <PermissionCardSection
            title="Settings pages"
            description="Map permissions to the setup pages available from Settings."
            selectedCount={settingsPermissionCards.filter((item) => item.codes.every((code) => permissionSet.has(code))).length}
            items={settingsPermissionCards}
            permissionSet={permissionSet}
            onToggle={(codes) => togglePermissionCodes(codes, !codes.every((code) => permissionSet.has(code)))}
            onToggleAll={() => togglePermissionCodes(settingsPermissionCards.flatMap((item) => item.codes), !settingsPermissionsSelected)}
            allSelected={settingsPermissionsSelected}
          />

          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => navigate('/settings/roles')} className="px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg">
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 bg-ink text-white rounded-lg shadow-soft disabled:opacity-60" disabled={saving}>
              {saving ? <Loader2 size={16} className="animate-spin" /> : 'Save'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

function PermissionCardSection({
  title,
  description,
  items,
  selectedCount,
  permissionSet,
  onToggle,
  onToggleAll,
  allSelected,
}: {
  title: string;
  description: string;
  items: Array<{ title: string; description: string; codes: string[]; codeLabel: string; icon: LucideIcon }>;
  selectedCount: number;
  permissionSet: Set<string>;
  onToggle: (codes: string[]) => void;
  onToggleAll: () => void;
  allSelected: boolean;
}) {
  return (
    <div className="card p-5 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Shield size={15} className="mt-0.5 text-slate-500" />
          <div>
            <h4 className="text-base font-semibold text-ink">{title}</h4>
            <p className="text-xs text-slate-500">{description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs text-slate-500">{selectedCount} selected</div>
          <button
            type="button"
            onClick={onToggleAll}
            className="rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 hover:border-ink hover:text-ink transition"
          >
            {allSelected ? 'Clear all' : 'Select all'}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-2">
        {items.map((item, index) => {
          const checked = item.codes.length > 0 && item.codes.every((code) => permissionSet.has(code));
          return (
            <label
              key={`${title}-${item.title}-${item.codeLabel}-${index}`}
              className={`flex items-start gap-2.5 rounded-lg border p-3 transition cursor-pointer ${
                checked ? 'border-ink/30 bg-ink/5 shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
            >
              <input
                type="checkbox"
                className="mt-0.5 accent-ink"
                checked={checked}
                disabled={!item.codes.length}
                onChange={() => onToggle(item.codes)}
              />
              <div className={`mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${checked ? 'bg-ink text-white' : 'bg-slate-100 text-slate-600'}`}>
                <item.icon size={14} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <div className="text-sm font-semibold text-slate-900">{item.title}</div>
                  {item.codeLabel ? (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-600">
                      {item.codeLabel}
                    </span>
                  ) : null}
                </div>
                <div className="mt-0.5 text-xs text-slate-500">{item.description}</div>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}

function resolveNavIcon(title: string): LucideIcon {
  const key = title.toLowerCase();
  if (key.includes('home') || key.includes('dashboard')) return LayoutDashboard;
  if (key.includes('command')) return ShieldCheck;
  if (key.includes('patient')) return Users;
  if (key.includes('message')) return MessageCircle;
  if (key.includes('appointment')) return CalendarDays;
  if (key.includes('triage')) return Stethoscope;
  if (key.includes('tele')) return Video;
  if (key.includes('prescription')) return Pill;
  if (key.includes('billing')) return Coins;
  if (key.includes('offer') || key.includes('package')) return Coins;
  if (key.includes('timesheet')) return CalendarDays;
  if (key.includes('lead')) return Users;
  if (key.includes('inventory')) return FlaskConical;
  if (key.includes('monitor')) return Radio;
  if (key.includes('report')) return BarChart3;
  if (key.includes('ai')) return ShieldCheck;
  if (key.includes('lab')) return FlaskConical;
  if (key.includes('radiology')) return ScanSearch;
  if (key.includes('population')) return Activity;
  return Settings;
}

function resolveSettingsIcon(title: string): LucideIcon {
  const key = title.toLowerCase();
  if (key.includes('organization')) return LayoutDashboard;
  if (key.includes('location')) return Activity;
  if (key.includes('team') || key.includes('practice user') || key.includes('external user')) return Users;
  if (key.includes('role')) return ShieldCheck;
  if (key.includes('provider')) return Stethoscope;
  if (key.includes('appointment') || key.includes('schedule')) return CalendarDays;
  if (key.includes('resource') || key.includes('room')) return LayoutDashboard;
  if (key.includes('tele')) return Video;
  if (key.includes('manual') || key.includes('document') || key.includes('medical form')) return FlaskConical;
  if (key.includes('bill')) return Coins;
  if (key.includes('inventory')) return ScanSearch;
  if (key.includes('treatment')) return Activity;
  return Settings;
}

function resolvePatientTabIcon(title: string): LucideIcon {
  const key = title.toLowerCase();
  if (key.includes('overview')) return ShieldCheck;
  if (key.includes('prescription')) return Pill;
  if (key.includes('document')) return FlaskConical;
  if (key.includes('vaccination')) return ShieldCheck;
  if (key.includes('note')) return MessageCircle;
  if (key.includes('form')) return LayoutDashboard;
  if (key.includes('test')) return ScanSearch;
  if (key.includes('treatment')) return Activity;
  if (key.includes('monitor')) return Radio;
  if (key.includes('portal')) return Users;
  if (key.includes('appointment')) return CalendarDays;
  if (key.includes('tele')) return Video;
  if (key.includes('invoice')) return Coins;
  return Shield;
}

function resolvePermissionIcon(code: string): LucideIcon {
  if (code.startsWith('nav:')) {
    const title = code.replace('nav:', '').replace(/_/g, ' ');
    return resolveNavIcon(title);
  }
  if (code.startsWith('settings:')) {
    const title = code.replace('settings:', '').replace(/_/g, ' ');
    return resolveSettingsIcon(title);
  }
  if (code.startsWith('patient_tab:')) {
    const title = code.replace('patient_tab:', '').replace(/_/g, ' ');
    return resolvePatientTabIcon(title);
  }
  return Shield;
}

function Input({
  label,
  value,
  onChange,
  required,
  error,
  placeholder,
  asSelect = false,
  options = [],
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  error?: string;
  placeholder?: string;
  asSelect?: boolean;
  options?: Array<{ value: string; label: string }>;
  disabled?: boolean;
}) {
  return (
    <label className="text-sm space-y-1 block">
      <span className="text-slate-600 font-medium flex items-center gap-1">
        {label} {required && <span className="text-rose-600">*</span>}
      </span>
      {asSelect ? (
        <select
          className={`w-full border rounded-lg px-3 py-2 bg-white text-sm ${error ? 'border-rose-300' : 'border-slate-200'} focus:ring-2 focus:ring-ink/10 outline-none`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        >
          <option value="" disabled>
            Select a role
          </option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : (
        <input
          className={`w-full border rounded-lg px-3 py-2 bg-white text-sm ${error ? 'border-rose-300' : 'border-slate-200'} focus:ring-2 focus:ring-ink/10 outline-none`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
        />
      )}
      {error && <p className="text-xs text-rose-600">{error}</p>}
    </label>
  );
}
